import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Check, X, Pencil, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatPhoneNumber, validatePhoneNumber, validateEmail } from '../../lib/validation';
import { phoneHref, safeContactHref } from '../../lib/contactHref';
import {
  formatFirstName,
  formatLastName,
  formatFullName,
  formatCompanyName,
  formatStreetAddress,
  formatCity,
  formatState,
} from '../../lib/formatting';

const AIPolishButton = React.lazy(
  () => import('../forms/public/fields/AIPolishButton').then(m => ({ default: m.AIPolishButton }))
);
const VoiceInputButton = React.lazy(
  () => import('../forms/public/fields/VoiceInputButton').then(m => ({ default: m.VoiceInputButton }))
);
const AIPolishModal = React.lazy(
  () => import('../forms/public/fields/AIPolishModal').then(m => ({ default: m.AIPolishModal }))
);

export interface InlineEditFieldProps {
  /** Current value */
  value?: string;
  /** Placeholder text shown when empty */
  placeholder: string;
  /** Called when value is saved */
  onSave: (value: string) => Promise<void>;
  /** Input type */
  type?: 'text' | 'email' | 'tel' | 'url' | 'date' | 'number';
  /** Enable multiline textarea */
  multiline?: boolean;
  /** Icon component to show before value */
  icon?: React.ComponentType<{ className?: string }>;
  /** Prefix for making value a clickable link (e.g., "tel:", "mailto:") */
  linkPrefix?: string;
  /** Custom click handler for the value link (overrides linkPrefix navigation) */
  onLinkClick?: (value: string) => void;
  /** Additional CSS classes for the container */
  className?: string;
  /** Disable editing */
  disabled?: boolean;
  /**
   * Keep the edit pencil visible instead of revealing it on hover.
   *
   * Opt-in, because this component has 74 call sites and hover-to-reveal is the
   * established treatment for a dense sidebar of editable rows. Turn it on where
   * the field is a headline rather than one row among many — a ticket's Short
   * Description, say, where nobody thinks to hover a sentence to discover it is
   * editable (Dillan, 2026-09-23).
   *
   * Worth knowing: hover does not exist on touch, so every call site WITHOUT
   * this prop has an edit affordance that is invisible on a phone or tablet.
   * That is a real gap, but fixing it app-wide is a bigger, separate decision
   * than this flag.
   */
  alwaysShowEditAffordance?: boolean;
  /** Enable real-time validation for phone/email types */
  enableValidation?: boolean;
  /** Enable real-time formatting for names, addresses, company names */
  enableFormatting?: boolean;
  /** Formatting type when enableFormatting=true */
  formattingType?: 'firstName' | 'lastName' | 'fullName' | 'company' | 'streetAddress' | 'city' | 'state';
  /** AI assist buttons (voice transcribe + text polish) — shown in editing mode */
  aiAssist?: {
    enablePolish?: boolean;
    enableVoice?: boolean;
    fieldContext?: string;
    polishTone?: 'professional' | 'technical' | 'casual';
  };
}

/**
 * Inline editable field component with validation and formatting support
 *
 * Features:
 * - Click to edit in place
 * - Empty values show "Add [placeholder]" prompt
 * - Enter to save, Escape to cancel
 * - Optional icon prefix
 * - Optional link behavior (opens in new tab)
 * - Supports text, email, phone, url, date, number types
 * - Supports multiline with textarea
 * - Phone number formatting and validation (when enableValidation=true)
 * - Email validation with typo detection (when enableValidation=true)
 * - Name, address, and company formatting (when enableFormatting=true)
 *
 * @example
 * ```tsx
 * // Phone validation
 * <InlineEditField
 *   value={lead.phone}
 *   placeholder="Phone"
 *   type="tel"
 *   icon={Phone}
 *   linkPrefix="tel:"
 *   enableValidation
 *   onSave={(value) => updateLead({ phone: value })}
 * />
 *
 * // Name formatting
 * <InlineEditField
 *   value={contact.firstName}
 *   placeholder="First Name"
 *   enableFormatting
 *   formattingType="firstName"
 *   onSave={(value) => updateContact({ firstName: value })}
 * />
 * ```
 */
export function InlineEditField({
  value,
  placeholder,
  onSave,
  type = 'text',
  multiline = false,
  icon: Icon,
  linkPrefix,
  onLinkClick,
  className = '',
  disabled = false,
  alwaysShowEditAffordance = false,
  enableValidation = false,
  enableFormatting = false,
  formattingType,
  aiAssist,
}: InlineEditFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationWarning, setValidationWarning] = useState<string | null>(null);
  const [polishModal, setPolishModal] = useState<{ isOpen: boolean; original: string; polished: string }>({ isOpen: false, original: '', polished: '' });
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Sync editValue when value prop changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || '');
    }
  }, [value, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Select all text for easy replacement
      if (inputRef.current instanceof HTMLInputElement || inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  // Handle input changes with formatting and validation
  const handleChange = (newValue: string) => {
    let processedValue = newValue;
    setValidationError(null);
    setValidationWarning(null);

    if (enableValidation) {
      // Format and validate phone numbers as user types
      if (type === 'tel') {
        processedValue = formatPhoneNumber(newValue);
        const validation = validatePhoneNumber(processedValue);
        if (!validation.isValid && processedValue.length > 0) {
          setValidationError(validation.error || null);
        } else if (validation.warning) {
          setValidationWarning(validation.warning);
        }
      }

      // Validate emails with typo detection
      if (type === 'email') {
        const validation = validateEmail(newValue);
        if (!validation.isValid && newValue.length > 0) {
          setValidationError(validation.error || null);
        } else if (validation.suggestion) {
          setValidationWarning(validation.suggestion);
        }
      }
    }

    // Formatting is applied on save (not during typing) to avoid trimming
    // trailing spaces mid-word (e.g., "LOS " before typing "GATOS").

    setEditValue(processedValue);
  };

  /** Apply formatting (city, address, names) and return the formatted value */
  const applyFormatting = (raw: string): string => {
    if (!enableFormatting || !formattingType) return raw;

    let formattingResult;
    switch (formattingType) {
      case 'firstName':
        formattingResult = formatFirstName(raw);
        break;
      case 'lastName':
        formattingResult = formatLastName(raw);
        break;
      case 'fullName':
        formattingResult = formatFullName(raw);
        break;
      case 'company':
        formattingResult = formatCompanyName(raw);
        break;
      case 'streetAddress':
        formattingResult = formatStreetAddress(raw);
        break;
      case 'city':
        formattingResult = formatCity(raw);
        break;
      case 'state':
        formattingResult = formatState(raw);
        break;
    }

    if (formattingResult) {
      // Show warnings (e.g., all-caps converted) - only for name formatting
      if ('warnings' in formattingResult && formattingResult.warnings && formattingResult.warnings.length > 0) {
        setValidationWarning(formattingResult.warnings[0]);
      }
      // Show suggestions (e.g., invalid state, address abbreviations)
      if (formattingResult.suggestions && formattingResult.suggestions.length > 0) {
        setValidationWarning(formattingResult.suggestions[0]);
      }
      return formattingResult.formatted;
    }
    return raw;
  };

  const handleSave = async () => {
    // Don't save if there's a validation error
    if (validationError) {
      toast.error(validationError);
      return;
    }

    // Apply formatting on save (not during typing)
    const formattedValue = applyFormatting(editValue);
    setEditValue(formattedValue);

    if (formattedValue === value) {
      setIsEditing(false);
      setValidationWarning(null);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(formattedValue);
      setIsEditing(false);
      setValidationWarning(null);
    } catch (error) {
      console.error('Failed to save:', error);
      // Keep editing mode open on error
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value || '');
    setIsEditing(false);
    setValidationError(null);
    setValidationWarning(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Don't save on blur if clicking save/cancel buttons
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget?.closest('[data-inline-edit-action]')) {
      return;
    }
    // Don't auto-save if there's a validation error
    if (validationError) {
      return;
    }
    handleSave();
  };

  // Get border/ring classes based on validation state
  const getInputClasses = () => {
    const base = 'flex-1 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 disabled:opacity-50';
    if (validationError) {
      return `${base} border-red-300 focus:ring-red-500`;
    }
    if (validationWarning) {
      return `${base} border-amber-300 focus:ring-amber-500`;
    }
    return `${base} border-sky-300 focus:ring-sky-500`;
  };

  // Editing mode
  if (isEditing) {
    return (
      <div className={`flex flex-col gap-1 ${className}`}>
        <div className="flex items-start gap-2">
          {Icon && <Icon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-2" />}
          {multiline ? (
            <div className="flex-1">
              <textarea
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                value={editValue}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                disabled={isSaving}
                rows={3}
                className={`w-full px-3.5 py-2.5 bg-slate-50 ring-1 ${
                  validationError ? 'ring-red-300 focus:ring-red-500' :
                  validationWarning ? 'ring-amber-300 focus:ring-amber-500' :
                  'ring-slate-200 focus:ring-[#3FA5FF]'
                } rounded-xl text-sm font-semibold text-slate-900 focus:ring-2 focus:bg-white outline-none transition-all resize-y disabled:opacity-50`}
                placeholder={placeholder}
              />
              {aiAssist && (aiAssist.enablePolish || aiAssist.enableVoice) && (
                <div className="flex items-center gap-1 mt-1">
                  <Suspense fallback={null}>
                    {aiAssist.enableVoice && (
                      <VoiceInputButton
                        fieldContext={aiAssist.fieldContext || placeholder}
                        currentValue={editValue}
                        onValueChange={(v) => { setEditValue(v); }}
                      />
                    )}
                    {aiAssist.enablePolish && editValue.trim() && (
                      <AIPolishButton
                        fieldContext={aiAssist.fieldContext || placeholder}
                        tone={aiAssist.polishTone || 'professional'}
                        currentValue={editValue}
                        onSuccess={(polished, original) => setPolishModal({ isOpen: true, original, polished })}
                      />
                    )}
                  </Suspense>
                </div>
              )}
            </div>
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type={type === 'tel' ? 'text' : type}
              value={editValue}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              disabled={isSaving}
              className={getInputClasses()}
              placeholder={type === 'tel' && enableValidation ? '(555) 555-5555' : placeholder}
            />
          )}
          <button
            data-inline-edit-action
            onClick={handleSave}
            disabled={isSaving || !!validationError}
            className="p-1 text-emerald-600 hover:bg-emerald-50 rounded disabled:opacity-50"
            title="Save"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            data-inline-edit-action
            onClick={handleCancel}
            disabled={isSaving}
            className="p-1 text-slate-400 hover:bg-slate-100 rounded disabled:opacity-50"
            title="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {validationError && (
          <span className="text-xs text-red-600 ml-6">{validationError}</span>
        )}
        {validationWarning && !validationError && (
          <span className="text-xs text-amber-600 ml-6">{validationWarning}</span>
        )}
        {polishModal.isOpen && (
          <Suspense fallback={null}>
            <AIPolishModal
              isOpen={polishModal.isOpen}
              originalText={polishModal.original}
              polishedText={polishModal.polished}
              fieldLabel={aiAssist?.fieldContext || placeholder}
              tone={aiAssist?.polishTone || 'professional'}
              enableRetry
              onClose={() => setPolishModal({ isOpen: false, original: '', polished: '' })}
              onUseText={(text) => { setEditValue(text); setPolishModal({ isOpen: false, original: '', polished: '' }); }}
            />
          </Suspense>
        )}
      </div>
    );
  }

  // Helper to enter edit mode with clean validation state
  const enterEditMode = () => {
    setValidationError(null);
    setValidationWarning(null);
    setIsEditing(true);
  };

  // Empty state - show "Add..." prompt
  if (!value) {
    return (
      <button
        onClick={() => !disabled && enterEditMode()}
        disabled={disabled}
        className={`flex items-center gap-2 text-slate-400 hover:text-sky-600 group disabled:cursor-not-allowed disabled:hover:text-slate-400 ${className}`}
      >
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-sm italic">Add {placeholder.toLowerCase()}</span>
        {!disabled && (
          <Pencil
            className={`w-3 h-3 ${alwaysShowEditAffordance ? '' : 'opacity-0 group-hover:opacity-100'}`}
          />
        )}
      </button>
    );
  }

  // Build the full link URL. A `mailto:` value must be ONE plain address (#6443): the stored cell is
  // free text, and `a@b.com?bcc=x@evil.com` would open the mail app with a hidden recipient. A refused
  // value gives '' and renders as plain text, like every other email link in the client.
  const getLinkUrl = () => {
    if (!value) return '';
    // Handle URLs - add https:// if missing protocol
    if (type === 'url') {
      if (!value.match(/^https?:\/\//)) {
        return `https://${value}`;
      }
      return value;
    }
    if (linkPrefix === 'mailto:') return safeContactHref('mailto', value) ?? '';
    // A phone dials its first number, digits only ('' for "Restricted": no link at all).
    if (linkPrefix === 'tel:') return phoneHref('tel', value);
    // Handle other link types (tel:, etc.)
    if (linkPrefix) {
      return `${linkPrefix}${value}`;
    }
    return '';
  };
  const linkUrl = getLinkUrl();

  // Determine if this field should be a clickable link
  // - onLinkClick is provided (custom handler like map overlay)
  // - linkPrefix is provided and the value makes a usable link (tel:, mailto:, etc.)
  // - OR type is 'url' (website links should always be clickable)
  const isClickableLink = !!onLinkClick || linkUrl !== '' || type === 'url';

  // Display mode with value
  const displayContent = (
    <span className={`block ${isClickableLink ? 'text-sky-600 hover:text-sky-700' : 'text-slate-700'}${multiline ? ' whitespace-pre-wrap' : ' truncate'}`}>
      {value}
      {isClickableLink && <ExternalLink className="w-3 h-3 inline ml-1 flex-shrink-0" />}
    </span>
  );

  return (
    <div className={`flex items-center gap-2 group min-w-0 ${className}`}>
      {Icon && <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />}
      {isClickableLink && onLinkClick ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (value) onLinkClick(value);
          }}
          className="hover:underline cursor-pointer text-left min-w-0 truncate"
        >
          {displayContent}
        </button>
      ) : isClickableLink ? (
        <a
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="hover:underline min-w-0 truncate"
        >
          {displayContent}
        </a>
      ) : (
        displayContent
      )}
      {!disabled && (
        <button
          onClick={enterEditMode}
          className={`p-1 text-slate-400 hover:text-sky-600 transition-opacity flex-shrink-0 ${
            alwaysShowEditAffordance ? '' : 'opacity-0 group-hover:opacity-100'
          }`}
          title="Edit"
        >
          <Pencil className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export default InlineEditField;
